import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";

const Chart = forwardRef(({ historicalPrices, darkMode, stopLevel, profitLevel }, ref) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const stopLossLineRef = useRef(null);
  const takeProfitLineRef = useRef(null);
  const visibleLogicalRangeRef = useRef(null);

  useEffect(() => {
    if (chartContainerRef.current && historicalPrices.length > 0) {
      const colors = {
        backgroundColor: darkMode ? "#141C29" : "#ffffff",
        textColor: darkMode ? "#ffffff" : "#000000",
        upColor: darkMode ? "#26a69a" : "#4caf50",
        downColor: darkMode ? "#ef5350" : "#f44336",
      };

      if (!chartRef.current) {
        chartRef.current = createChart(chartContainerRef.current, {
          width: chartContainerRef.current.clientWidth,
          height: 400,
          layout: {
            background: { type: "solid", color: colors.backgroundColor },
            textColor: colors.textColor,
          },
          grid: {
            vertLines: { color: darkMode ? "#2e2e2e" : "#d0d0d0" },
            horzLines: { color: darkMode ? "#2e2e2e" : "#d0d0d0" },
          },
          crosshair: {
            mode: CrosshairMode.Normal,
          },
          rightPriceScale: {
            borderColor: darkMode ? "#2e2e2e" : "#d0d0d0",
          },
          timeScale: {
            borderColor: darkMode ? "#2e2e2e" : "#d0d0d0",
            timeVisible: true,
            secondsVisible: false,
          },
        });

        candlestickSeriesRef.current = chartRef.current.addCandlestickSeries({
          upColor: colors.upColor,
          downColor: colors.downColor,
          borderUpColor: colors.upColor,
          borderDownColor: colors.downColor,
          wickUpColor: colors.upColor,
          wickDownColor: colors.downColor,
        });

        stopLossLineRef.current = chartRef.current.addLineSeries({
          color: '#FF9800',
          lineWidth: 2,
          lineStyle: 0,
        });

        takeProfitLineRef.current = chartRef.current.addLineSeries({
          color: '#049981',
          lineWidth: 2,
          lineStyle: 0,
        });

        // Save the visible range when user interacts with the chart
        chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(range => {
          visibleLogicalRangeRef.current = range;
        });
      }

      candlestickSeriesRef.current.setData(historicalPrices);

      const lastTime = historicalPrices[historicalPrices.length - 1].time;
      stopLossLineRef.current.setData([
        { time: historicalPrices[0].time, value: stopLevel },
        { time: lastTime, value: stopLevel },
      ]);

      takeProfitLineRef.current.setData([
        { time: historicalPrices[0].time, value: profitLevel },
        { time: lastTime, value: profitLevel },
      ]);

      // Only fit content if we don't have a saved range
      if (!visibleLogicalRangeRef.current) {
        chartRef.current.timeScale().fitContent();
      } else {
        chartRef.current.timeScale().setVisibleLogicalRange(visibleLogicalRangeRef.current);
      }

      const handleResize = () => {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [historicalPrices, darkMode, stopLevel, profitLevel]);

  // Function to update the chart with new data
  const updateChart = (newCandle) => {
    if (candlestickSeriesRef.current) {
      candlestickSeriesRef.current.update(newCandle);
    }
    if (stopLossLineRef.current && takeProfitLineRef.current) {
      const lastTime = newCandle.time;
      stopLossLineRef.current.update({ time: lastTime, value: stopLevel });
      takeProfitLineRef.current.update({ time: lastTime, value: profitLevel });
    }
    
    // Preserve the visible range after updating
    if (visibleLogicalRangeRef.current) {
      chartRef.current.timeScale().setVisibleLogicalRange(visibleLogicalRangeRef.current);
    }
  };

  // Expose the updateChart function
  useImperativeHandle(ref, () => ({
    updateChart
  }));

  return <div ref={chartContainerRef} className="w-full h-96 mb-8 border-2 border-slate-700"></div>;
});

export default Chart;